import { Link, useLocation, useNavigate } from "react-router-dom";
import { clearToken } from "../lib/api";
import "./AppLayout.css";

/**
 * UKVI SMS-style shell:
 *   navy banner ("UK Visas & Immigration" + crest)
 *   → purple breadcrumb strip with gold accent
 *   → left sidebar menu (white bg, purple sub-arrows, red top-level arrows)
 *   → white content area.
 *
 * Inspired by the visual style of the Home Office Sponsorship Management System.
 * Not affiliated with, endorsed by, or impersonating the UK Government.
 */
export default function AppLayout({ breadcrumb = [], activeMenu, seeAlso, children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("cos_user") || "null");
  const isAdmin = user?.role === "admin";

  const logout = () => {
    clearToken();
    localStorage.removeItem("cos_user");
    navigate("/login");
  };

  // Sidebar menu — mirrors the SMS Workers navigation.
  // Items without a `to` render as non-clickable placeholders (feature not built yet).
  const menu = [
    { key: "home", label: "Home", to: "/", top: true },
    { key: "licence", label: "Licence summary, applications and services", top: true },
    {
      key: "workers",
      label: "Workers",
      top: true,
      open: true,
      children: [
        { key: "create", label: "Create and assign", to: "/cos/new" },
        { key: "defined", label: "Defined CoS", to: "/cos" },
        {
          key: "view",
          label: "View CoS",
          to: "/cos",
          open: true,
          children: [
            { key: "search-cos", label: "Search for CoS", to: "/cos/search" },
            { key: "search-batches", label: "Search for batch(es) of CoS" },
            { key: "search-groups", label: "Search for groups of CoS" },
            { key: "transfer-groups", label: "Transfer group of CoS" },
          ],
        },
        { key: "sponsorship-duties", label: "Sponsorship duties" },
        { key: "manage-addresses", label: "Manage work addresses" },
      ],
    },
    ...(isAdmin
      ? [{ key: "users", label: "Manage users", to: "/admin/users", top: true }]
      : []),
    { key: "change-password", label: "Change password", top: true },
    { key: "contact", label: "Contact", top: true },
    { key: "logout", label: "Log out", top: true, onClick: logout },
  ];

  const isActive = (key, to) =>
    activeMenu === key || (to && location.pathname === to && !activeMenu);

  const renderItem = (item, depth = 0) => {
    const active = isActive(item.key, item.to);
    const cls = [
      "sms-nav",
      depth === 0 ? "sms-nav-top" : "sms-nav-sub",
      depth === 0 && item.children ? "section-parent" : "",
      active ? "active" : "",
    ]
      .filter(Boolean)
      .join(" ");
    const inner = (
      <>
        <span className={depth === 0 ? "sms-arrow-red" : "sms-arrow-purple"} aria-hidden />
        <span className="sms-nav-label">{item.label}</span>
      </>
    );
    let node;
    if (item.onClick) {
      node = (
        <button
          type="button"
          onClick={item.onClick}
          className={cls}
          data-testid={`nav-${item.key}`}
        >
          {inner}
        </button>
      );
    } else if (item.to) {
      node = (
        <Link to={item.to} className={cls} data-testid={`nav-${item.key}`}>
          {inner}
        </Link>
      );
    } else {
      node = (
        <span className={`${cls} disabled`} data-testid={`nav-${item.key}`}>
          {inner}
        </span>
      );
    }
    const subCls = [
      "sms-submenu",
      depth > 0 ? "deep" : "",
      depth === 0 && item.children ? "workers-box" : "",
    ]
      .filter(Boolean)
      .join(" ");
    return (
      <li key={item.key}>
        {node}
        {item.children && item.open && (
          <ul className={subCls}>
            {item.children.map((c) => renderItem(c, depth + 1))}
          </ul>
        )}
      </li>
    );
  };

  return (
    <div className="sms-shell">
      <header className="sms-banner" role="banner">
        <div className="sms-banner-inner">
          <div className="sms-brand">
            <img
              src="/crest.svg"
              alt=""
              className="sms-brand-crest"
              width="56"
              height="50"
            />
            <span className="sms-brand-text">UK Visas &amp; Immigration</span>
          </div>
          {user && (
            <div className="sms-userchip" data-testid="user-chip">
              Signed in as <strong>{user.name}</strong>
              {isAdmin && <span className="sms-adminpill">Admin</span>}
            </div>
          )}
        </div>
      </header>

      <nav className="sms-breadcrumb" aria-label="breadcrumb">
        <div className="sms-peak-left" aria-hidden />
        <div className="sms-peak-right" aria-hidden />
        <div className="sms-drop-right" aria-hidden />
        <div className="sms-breadcrumb-inner">
          <span className="sms-crumb-lead">You are here</span>
          {breadcrumb.map((c, i) => (
            <span key={i} className="sms-crumb">
              <span className="sms-crumb-sep">&gt;</span>
              {c.to ? <Link to={c.to}>{c.label}</Link> : <span>{c.label}</span>}
            </span>
          ))}
        </div>
      </nav>

      <div className={`sms-body ${seeAlso ? "with-aside" : ""}`}>
        <aside className="sms-sidebar" aria-label="Main navigation">
          <ul>{menu.map((item) => renderItem(item, 0))}</ul>
        </aside>

        <main className="sms-content">{children}</main>

        {seeAlso && (
          <aside className="sms-seealso" aria-label="See also">
            <div className="sms-seealso-title">SEE ALSO</div>
            <ul>
              {seeAlso.map((s, i) => (
                <li key={i}>
                  <span className="sms-arrow-purple" aria-hidden />
                  {s.href ? (
                    <a href={s.href} target={s.newTab ? "_blank" : undefined} rel="noreferrer">
                      {s.label}
                    </a>
                  ) : (
                    <span>{s.label}</span>
                  )}
                </li>
              ))}
            </ul>
          </aside>
        )}
      </div>
    </div>
  );
}
